import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrderLocationChangePreview } from '@hero/api';
import { computeAmountPreview } from '@hero/core';
import { ChoiceCard } from '../../../app/ChoiceCard';
import { ModalForm } from '../../../app/Modal';
import { Price } from '../../../app/Price/Price';
import { PriceCategoryBadge } from '../../../app/PriceCategoryBadge';
import { PriceInput } from '../../../app/PriceInput';

/** What the user picked, ready to be turned into a PATCH. */
export type LocationChangePriceChoice = { mode: 'route' } | { mode: 'custom'; price: number };

export interface LocationChangePriceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: OrderLocationChangePreview;
  /** Applies the destination change with the chosen price. */
  onConfirm: (choice: LocationChangePriceChoice) => void;
  isSaving?: boolean;
}

/**
 * Cross-city destination change — show the price consequence, then let the
 * user pick one.
 *
 * This modal exists because the backend stopped refusing city changes. It
 * now reprices the caller's contract leg, and repricing without showing the
 * number first is the one outcome worse than the old hard error: the user's
 * money moves and nothing tells them.
 *
 * Two options, picked then saved — the same shape as the price-edit drawer
 * (`OrderPriceEditDrawer`), which also offers "the rate on file" vs "a
 * number I type". Selecting is separate from committing so the user can
 * compare the two prices without either click being destructive.
 *
 * Both options are named for *what they are* — "Price for the new route" and
 * "Custom price" — with where the rate came from carried by a colour-coded
 * chip beside the first. Naming the option after its origin ("Agreement
 * price") made the pair read as two unrelated things rather than two answers
 * to one question. The chip's wording and colours are the drawer's, so a
 * green "Agreement price" means the same here as everywhere else.
 *
 * When the new lane has no rate, the custom option is all that's offered and
 * it comes prefilled with the current price, which makes "just keep what we
 * had" a single click.
 *
 * Blocked changes never reach here; the caller renders the reason from
 * `preview.blockedReason` instead, because there is no choice to offer.
 *
 * See ADR-020.
 */
export const LocationChangePriceModal: React.FC<LocationChangePriceModalProps> = ({
  open,
  onOpenChange,
  preview,
  onConfirm,
  isSaving = false,
}) => {
  const { t } = useTranslation(['m-order', 'c-main']);

  const routePrice = preview.hasRateOnNewRoute ? preview.resolvedPrice : undefined;
  const hasRouteOption = routePrice != null;

  const [mode, setMode] = useState<LocationChangePriceChoice['mode'] | null>(null);
  const [customPrice, setCustomPrice] = useState<number | undefined>(undefined);

  // Seed each time the modal opens for a new candidate route. The rate on
  // file is the default answer whenever there is one — it's what the order
  // would have been priced at had it been created on this lane — so Save is
  // reachable in one click and the custom field stays there for the minority
  // who want to override it.
  useEffect(() => {
    if (!open) return;
    setMode(hasRouteOption ? 'route' : 'custom');
    setCustomPrice(preview.previousPrice);
  }, [open, hasRouteOption, preview.previousPrice]);

  const isCustomPriceValid = customPrice != null && customPrice > 0;

  /**
   * The load price the user has settled on, or `undefined` while the choice
   * is incomplete — which is also what gates Save.
   */
  const chosenPrice =
    mode === 'route'
      ? routePrice
      : mode === 'custom' && isCustomPriceValid
      ? customPrice
      : undefined;

  /**
   * The tax-inclusive total the chosen load price lands on.
   *
   * Derived through `computeAmountPreview` with the leg's own fees and tax
   * so the number shown here is the number the backend will write — an
   * inline `× 1.15` would drift the moment a fee or the VAT rate changed.
   */
  const chosenTotal = useMemo(() => {
    if (chosenPrice == null) return undefined;
    return computeAmountPreview({
      itemsOriginalSubtotal: chosenPrice,
      fees: { applied: preview.fees },
      taxId: preview.taxId,
      currency: preview.currency,
    }).total;
  }, [chosenPrice, preview.currency, preview.fees, preview.taxId]);

  const handleSave = () => {
    if (mode === 'route') return onConfirm({ mode: 'route' });
    if (mode === 'custom' && customPrice != null) onConfirm({ mode: 'custom', price: customPrice });
  };

  return (
    <ModalForm
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={t('location_change.title', 'The city changed')}
      description={t(
        'location_change.description',
        'This moves the order to a different city, so the price needs a decision.',
      )}
      onSave={handleSave}
      // Naming the amount on the button makes the commit self-describing —
      // "Save 1,000" leaves nothing to scroll back up and re-check. Built
      // from `<Price>` rather than an interpolated string so the Riyal glyph
      // matches every other amount in the app. Left to ModalForm's plain
      // "Save" while the choice is incomplete, which is also when the button
      // is disabled.
      saveText={
        chosenPrice != null ? (
          <span className="inline-flex items-baseline gap-1">
            {t('c-main:crud_buttons.save', 'Save')}
            <Price amount={chosenPrice} currency={preview.currency} />
          </span>
        ) : undefined
      }
      disableSave={chosenPrice == null}
      isLoading={isSaving}
      cancelText={t('location_change.cancel', 'Cancel')}
      size="md">
      <div className="space-y-4 text-sm">
        <div className="flex flex-col gap-1.5 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
          <span className="text-xs font-medium text-slate-500">
            {t('location_change.route', 'Route')}
          </span>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-slate-500 line-through">{preview.previousRouteLabel}</span>
            <span className="font-medium text-slate-900">{preview.newRouteLabel}</span>
          </div>
          {preview.distanceKm != null ? (
            <span className="text-xs text-slate-500">
              {t('location_change.distance', '{{km}} km from the original stop', {
                km: Math.round(preview.distanceKm),
              })}
            </span>
          ) : null}
        </div>

        <div className="space-y-2">
          {hasRouteOption ? (
            <ChoiceCard
              selected={mode === 'route'}
              onSelect={() => setMode('route')}
              disabled={isSaving}
              label={
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {t('location_change.new_route_price', 'Price for the new route')}
                  {/* Provenance sits *beside* the option name, never as it:
                      naming the option after its origin made the pair read
                      as unrelated things rather than two answers to one
                      question. */}
                  <PriceCategoryBadge category={preview.resolvedCategory} />
                </span>
              }>
              <Price
                amount={routePrice}
                currency={preview.currency}
                className="text-base font-semibold"
              />
            </ChoiceCard>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 px-3 py-2.5">
              <span className="text-xs font-medium text-slate-500">
                {t('location_change.new_route_price', 'Price for the new route')}
              </span>
              <p className="text-sm text-slate-600">
                {t('location_change.no_rate', 'Nothing on file for this route yet.')}
              </p>
            </div>
          )}

          <ChoiceCard
            selected={mode === 'custom'}
            onSelect={() => setMode('custom')}
            disabled={isSaving}
            label={
              <>
                {t('drawer.price_edit.custom_price_badge', { defaultValue: 'Custom price' })}{' '}
                <span className="font-normal text-primary-600">
                  {t('location_change.before_tax_note', '(before tax)')}
                </span>
              </>
            }
            hint={
              mode === 'custom'
                ? undefined
                : t('location_change.custom_hint', 'Set the price for this order yourself.')
            }>
            {mode === 'custom' ? (
              <PriceInput
                value={customPrice}
                onChange={setCustomPrice}
                currency={preview.currency}
                disabled={isSaving}
              />
            ) : (
              <Price
                amount={preview.previousPrice}
                currency={preview.currency}
                className="text-base font-semibold text-slate-500"
              />
            )}
          </ChoiceCard>
        </div>

        {/*
         * The one number nobody can derive by eye: what the chosen load
         * price becomes once this leg's fees and tax are applied. Hidden
         * when the leg is untaxed and fee-free, where it would just repeat
         * the load price back.
         */}
        {chosenTotal != null && chosenTotal !== chosenPrice ? (
          <p className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
            {t('location_change.total_with_tax', 'Total amount with tax')}
            <Price
              amount={chosenTotal}
              currency={preview.currency}
              size="xs"
              className="font-semibold text-slate-700"
            />
          </p>
        ) : null}

        {preview.otherLegsUnchanged > 0 ? (
          <p className="text-xs text-slate-500">
            {t(
              'location_change.other_legs',
              'Only your price changes. {{count}} other party on this order keeps theirs.',
              { count: preview.otherLegsUnchanged },
            )}
          </p>
        ) : null}
      </div>
    </ModalForm>
  );
};
